import { Box, Paper, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import { quoteModern } from "../../../style/style";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
const clean = (value?: any) => String(value ?? "").trim();

const formatUSD = (value?: any) =>
  Number(value ?? 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const BasicServiceItem = ({ service }) => {
  const originalServiceName = clean(service?.service) || clean(service?.scopeOfWork?.name) || "Unnamed Service";

  const serviceNameOverride = clean(service?.serviceNameOverride) || clean(service?.scopeOfWork?.serviceNameOverride);

  const serviceName = serviceNameOverride || originalServiceName;
  const hasServiceNameOverride = Boolean(serviceNameOverride);

  const price = Number(service?.price ?? 0);
  const units = Number(service?.units ?? 0);
  const total = Number(service?.total ?? price * units);

  const measure = clean(service?.measure);
  const notes = clean(service?.notes);

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        border: `1px solid ${quoteModern.colors.line}`,
        boxShadow: quoteModern.colors.shadow,
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* SERVICE NAME */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.45,
              borderRadius: 999,
              bgcolor: quoteModern.colors.orangeSoft,
              color: quoteModern.colors.orange,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 14 }} />

            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                lineHeight: 1,
              }}
            >
              Basic service
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 1,
              fontSize: 17,
              fontWeight: 900,
              color: quoteModern.colors.textPrimary,
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {serviceName}
          </Typography>

          {hasServiceNameOverride && (
            <Box
              sx={{
                mt: 0.65,
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.45,
                px: 1,
                py: 0.45,
                borderRadius: 999,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                border: `1px solid ${quoteModern.colors.line}`,
                maxWidth: "100%",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: quoteModern.colors.textSecondary,
                  flexShrink: 0,
                }}
              >
                Original service:
              </Typography>

              <Typography
                component="span"
                title={originalServiceName}
                sx={{
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: quoteModern.colors.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {originalServiceName}
              </Typography>
            </Box>
          )}
        </Box>

        {/* TOTAL */}
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            lineHeight: 1.2,
            color: quoteModern.colors.orange,
            flexShrink: 0,
          }}
        >
          {formatUSD(total)}
        </Typography>
      </Box>

      {/* PRICE INFORMATION */}
      <Box
        sx={{
          mt: 1.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          minWidth: 190,
          maxWidth: "100%",
          px: 1.25,
          py: 0.9,
          borderRadius: 2,
          bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
          border: `1px solid ${quoteModern.colors.line}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.75,
            color: quoteModern.colors.orange,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AttachMoneyOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={quoteModern.labelSx}>Price</Typography>

          <Typography
            sx={{
              mt: 0.3,
              fontSize: 12.5,
              fontWeight: 800,
              color: quoteModern.colors.textPrimary,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
            }}
          >
            {formatUSD(price)}
            <Box component="span" sx={{ mx: 0.65, color: "text.secondary" }}>
              •
            </Box>
            {units} unit{units !== 1 ? "s" : ""}
            {measure && (
              <>
                <Box component="span" sx={{ mx: 0.65, color: "text.secondary" }}>
                  •
                </Box>

                {measure}
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {/* NOTES */}
      {notes && (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            color: quoteModern.colors.textSecondary,
          }}
        >
          <NotesOutlinedIcon
            sx={{
              mt: 0.1,
              fontSize: 16,
              color: quoteModern.colors.orange,
              flexShrink: 0,
            }}
          />

          <Typography
            sx={{
              fontSize: 12.5,
              lineHeight: 1.4,
              wordBreak: "break-word",
            }}
          >
            <Box component="span" sx={{ fontWeight: 800 }}>
              Notes:
            </Box>{" "}
            {notes}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BasicServiceItem;
